/**
 * The list of modules and their configuration.
 *
 * Copyright © Ircam Amplify 2024-2026
 */

import { WebAppUtil } from '@/utils/WebAppUtil'
import { TextUtil } from '@amplify3/duplo-back-end'
import {
  ModuleConfig,
  ModuleConfigAiMusicDetector,
  ModuleConfigAiReEditor,
  ModuleConfigAudioTextAligner,
  ModuleConfigEditBox,
  ModuleConfigLoopExtractor,
  ModuleConfigStemSeparator,
  ModuleConfigTalkAnalyzer,
  ModuleConfigTimePitchControl,
  MultiInputMode,
} from './ModuleConfig'
import {
  ChannelMode,
  FileFieldConfig,
  getMimeTypeFromFileFormatList,
  toChannelCountList,
} from './ModuleDef'
import { ModuleKey } from './ModuleInfo'
import { ModuleInfoList } from './ModuleInfoList'
import { FileConstraints } from './ModuleInputConfig'
import { ModuleParam, ModuleParamType } from './ModuleParam'

export const STORAGE_SERVICE_NAME = 'storage'

/**
 * Common API input names.
 */
export enum InputName {
  LyricsUrl = 'lyricsUrl',
  ScriptUrl = 'scriptUrl',
  AudioUrl = 'audioUrl',
  AudioUrlList = 'audioUrlList',
}

const makeApiDocUrl = (tagName: string) => {
  const api_doc_uri = `api#tag/${tagName}/Overview`
  const env = WebAppUtil.getEnv()
  if (env) {
    return new URL(api_doc_uri, env.VITE_API_DOCS_BASE_URI).href
  } else {
    throw new Error('env undefined')
  }
}

/**
 * Generate the text of the tooltip of a module.
 * @returns A string containing the tooltip text.
 */
const getToolTipText = ({
  formats,
  minimumDurationSeconds,
  maximumDurationSeconds,
  channelModes,
  additionalInfo,
}: FileFieldConfig) => {
  const fileFormats = formats
    .map((i) => i.toUpperCase())
    .join(', ')
    .replace(/,([^,]*)$/, ' or$1')

  const minDuration = minimumDurationSeconds
    ? minimumDurationSeconds == 0
      ? ''
      : `min ${TextUtil.formatDuration(minimumDurationSeconds)}`
    : ''

  const maxDuration = maximumDurationSeconds
    ? maximumDurationSeconds == 0
      ? ''
      : `max ${TextUtil.formatDuration(maximumDurationSeconds)}`
    : ''
  const channels = channelModes?.join(' or ') || ''

  return [fileFormats, minDuration, maxDuration, channels, additionalInfo]
    .filter(Boolean)
    .join(', ')
}

const makeInputFileFieldTipAndConstraints = ({
  formats,
  minimumDurationSeconds,
  maximumDurationSeconds,
  channelModes,
  additionalInfo,
}: FileFieldConfig): FileConstraints => {
  return {
    tip: getToolTipText({
      formats,
      minimumDurationSeconds,
      maximumDurationSeconds,
      channelModes,
      additionalInfo,
    }),
    mimeTypeList: getMimeTypeFromFileFormatList(formats),
    channelModeList: channelModes ?? undefined,
    channelCountList: toChannelCountList(channelModes),
    minDuration: minimumDurationSeconds,
    maxDuration: maximumDurationSeconds,
    acceptedChannels:
      channelModes?.length == 1
        ? channelModes[0] == ChannelMode.Mono
          ? 1
          : 2
        : undefined,
  }
}

/**
 * The order of the modules by product design.
 */
export const ModuleOrder: ModuleKey[] = [
  ModuleKey.AiMusicDetector,
  ModuleKey.AiSpeechDetector,
  ModuleKey.StereoToSpatial,
  ModuleKey.AiReEditor,
  ModuleKey.StemSeparator,
  ModuleKey.MusicTaggerAll,
  ModuleKey.MusicTaggerGenre,
  ModuleKey.MusicTaggerMood,
  ModuleKey.MusicTaggerInstrument,
  ModuleKey.MusicTaggerRhythm,
  ModuleKey.MusicTaggerKey,
  ModuleKey.MusicTaggerSummary,
  ModuleKey.MusicTaggerStructure,
  ModuleKey.MusicTaggerBeats,
  ModuleKey.MusicTaggerChords,
  ModuleKey.LoopExtractor,
  ModuleKey.QualityCheck,
  ModuleKey.AudioTextAligner,
  ModuleKey.TimePitchControl,
  ModuleKey.TalkAnalyzer,
  ModuleKey.CoverDetector,
  ModuleKey.InfringementDetector,
  ModuleKey.LoudnessAnalyzer,
  ModuleKey.Converter,
  ModuleKey.EditCut,
  ModuleKey.EditFadeIn,
  ModuleKey.EditFadeOut,
  ModuleKey.EditMix,
  ModuleKey.EditTransition,
  ModuleKey.EditMute,
  ModuleKey.EditPanning,
]

/**
 * The list of modules sorted by a type.
 *
 * Note: the order of the modules to display in selection view is defined
 * by the list ModuleOrder.
 * If a ModuleKey is added to ModuleConfigList, please check that it is
 * also added to ModuleOrder in the right place.
 */
export const ModuleConfigList: Record<ModuleKey, ModuleConfig> = {
  [ModuleKey.AiMusicDetector]: new ModuleConfigAiMusicDetector({
    moduleKey: ModuleKey.AiMusicDetector,
    info: ModuleInfoList[ModuleKey.AiMusicDetector],
    api_doc_url: makeApiDocUrl('AI-Music-Detector'),
    inputList: [
      {
        name: InputName.AudioUrlList,
        title: 'Audio Files',
        ...makeInputFileFieldTipAndConstraints({
          formats: [
            // Ignore 'aif': 'aiff' give support automatically to 'aif' format.
            // Adding 'aif' here will makes TaskCreationPage show
            // "AIF, AIFF" files supported, which is redundant.
            'aiff',
            'flac',
            'm4a',
            'mp3',
            'mp4',
            'ogg',
            'wav',
          ],
          minimumDurationSeconds: 3,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
          additionalInfo: 'max 20 tracks',
        }),
        multiple: true,
        minMultipleInputCount: 1,
      },
    ],
    paramList: [
      new ModuleParam<boolean>({
        name: 'aidetectorParamGroup',
        title: 'Additional analysis',
        type: ModuleParamType.SwitchGroup,
        tip: 'Analysis of the evolution over time',
        options: [
          {
            name: 'timeAnalysis',
            value: false, // ignored for switchGroup
            title: 'Time analysis',
          },
          // TODO: enable stem analysis switch when backend supports it.
          // {
          //   name: 'stemAnalysis',
          //   value: false, // ignored for switchGroup
          //   title: 'Stem analysis',
          // },
        ],
      }),
    ],
  }),

  [ModuleKey.AiSpeechDetector]: new ModuleConfig({
    moduleKey: ModuleKey.AiSpeechDetector,
    info: ModuleInfoList[ModuleKey.AiSpeechDetector],
    api_doc_url: makeApiDocUrl('AI-Speech-Detector'),
    inputList: [
      {
        name: InputName.AudioUrlList,
        title: 'Audio Files',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 3,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
          additionalInfo: 'max 20 tracks',
        }),
        multiple: true,
        minMultipleInputCount: 1,
      },
    ],
  }),

  [ModuleKey.StereoToSpatial]: new ModuleConfig({
    moduleKey: ModuleKey.StereoToSpatial,
    info: ModuleInfoList[ModuleKey.StereoToSpatial],
    api_doc_url: makeApiDocUrl('Stereo-to-Spatial'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Stereo File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac'],
          minimumDurationSeconds: 1,
          maximumDurationSeconds: 9.5 * 60,
          channelModes: [ChannelMode.Stereo],
        }),
      },
    ],
    paramList: [
      new ModuleParam<number>({
        name: 'presetId',
        title: 'Preset',
        type: ModuleParamType.Button,
        options: [
          { value: 1, title: '1 Subtle' },
          { value: 2, title: '2 Gentle', defaultValue: true },
          { value: 3, title: '3 Medium' },
          { value: 4, title: '4 Intense' },
          { value: 5, title: '5 Maximum' },
        ],
        tip: 'Several spatialization presets are available, allowing the user to create a greater or more subtle effect. (Default : 2)',
      }),
      new ModuleParam<string>({
        name: 'surroundFormat',
        title: 'Surround format',
        type: ModuleParamType.Button,
        options: [
          { value: 'atmos', title: 'Atmos', defaultValue: true },
          { value: 'iamf', title: 'Atmos + IAMF' },
        ],
        tip: 'Select the format of the surround file to generate',
      }),
    ],
  }),

  [ModuleKey.AiReEditor]: new ModuleConfigAiReEditor({
    moduleKey: ModuleKey.AiReEditor,
    info: ModuleInfoList[ModuleKey.AiReEditor],
    api_doc_url: makeApiDocUrl('AI-Re-editor'),
    multiplicity: MultiInputMode.OneSingleJob,
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 60,
          maximumDurationSeconds: 3600,
          channelModes: [ChannelMode.Stereo],
        }),
      },
    ],
    paramList: [
      new ModuleParam<any>({
        name: 'targetDuration',
        title: 'Target Duration',
        type: ModuleParamType.Duration,
        min: 10,
        max: 10000,
        step: 1,
        unit: 's',
        tip: 'The desired duration of the audio extract, in seconds. The minimum is 10s and the maximum is your original audio duration.',
      }),
      new ModuleParam<string>({
        name: 'optimization',
        title: 'Optimization',
        type: ModuleParamType.Button,
        options: [
          { value: 'quality', title: 'Quality', defaultValue: true },
          { value: 'time', title: 'Time' },
        ],
        tip: 'Desired optimization parameter. Default value is set to "quality".',
        tip2: (
          <p>
            <b>Quality : </b>
            the most quality sounding track edited, without focusing on Target
            Duration. For long and complex tracks.
            <br />
            <b>Time : </b>
            prioritizing reaching your Target Duration, with a risk of higher
            quality loss. For simpler tracks.
            <br />
          </p>
        ),
      }),
    ],
    minParamCount: 2,
  }),

  [ModuleKey.StemSeparator]: new ModuleConfigStemSeparator({
    moduleKey: ModuleKey.StemSeparator,
    info: ModuleInfoList[ModuleKey.StemSeparator],
    api_doc_url: makeApiDocUrl('Stem-Separator'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          maximumDurationSeconds: 90 * 60,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
    paramList: [
      new ModuleParam<string>({
        name: 'stemSetting',
        title: 'Select the stems',
        type: ModuleParamType.Button,
        options: [
          { value: 'all', title: 'All stems', defaultValue: true },
          { value: 'vocals', title: 'Vocals and instrumental' },
        ],
        tip: 'Choose "Vocals and instrumental" to extract one vocal and one instrumental track. Select “All stems” to separate into vocals, bass, drums, and other sounds.',
      }),
      new ModuleParam<ChannelMode>({
        name: 'channelMode',
        title: 'Channel Mode',
        type: ModuleParamType.Button,
        options: [
          { value: ChannelMode.Stereo, title: 'Stereo', defaultValue: true },
          { value: ChannelMode.Mono, title: 'Mono' },
        ],
        tip: 'Mono option will down-mix to mono audio processing. Default is stereo and will apply no change.',
      }),
    ],
  }),

  [ModuleKey.MusicTaggerAll]: new ModuleConfig({
    moduleKey: ModuleKey.MusicTaggerAll,
    info: ModuleInfoList[ModuleKey.MusicTaggerAll],
    api_doc_url: makeApiDocUrl('Music-Tagger'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.MusicTaggerGenre]: new ModuleConfig({
    moduleKey: ModuleKey.MusicTaggerGenre,
    info: ModuleInfoList[ModuleKey.MusicTaggerGenre],
    api_doc_url: makeApiDocUrl('Music-Tagger'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.MusicTaggerMood]: new ModuleConfig({
    moduleKey: ModuleKey.MusicTaggerMood,
    info: ModuleInfoList[ModuleKey.MusicTaggerMood],
    api_doc_url: makeApiDocUrl('Music-Tagger'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.MusicTaggerInstrument]: new ModuleConfig({
    moduleKey: ModuleKey.MusicTaggerInstrument,
    info: ModuleInfoList[ModuleKey.MusicTaggerInstrument],
    api_doc_url: makeApiDocUrl('Music-Tagger'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.MusicTaggerRhythm]: new ModuleConfig({
    moduleKey: ModuleKey.MusicTaggerRhythm,
    info: ModuleInfoList[ModuleKey.MusicTaggerRhythm],
    api_doc_url: makeApiDocUrl('Music-Tagger'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.MusicTaggerKey]: new ModuleConfig({
    moduleKey: ModuleKey.MusicTaggerKey,
    info: ModuleInfoList[ModuleKey.MusicTaggerKey],
    api_doc_url: makeApiDocUrl('Music-Tagger'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.MusicTaggerSummary]: new ModuleConfig({
    moduleKey: ModuleKey.MusicTaggerSummary,
    info: ModuleInfoList[ModuleKey.MusicTaggerSummary],
    api_doc_url: makeApiDocUrl('Music-Tagger'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.MusicTaggerStructure]: new ModuleConfig({
    moduleKey: ModuleKey.MusicTaggerStructure,
    info: ModuleInfoList[ModuleKey.MusicTaggerStructure],
    api_doc_url: makeApiDocUrl('Music-Tagger'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.MusicTaggerBeats]: new ModuleConfig({
    moduleKey: ModuleKey.MusicTaggerBeats,
    info: ModuleInfoList[ModuleKey.MusicTaggerBeats],
    api_doc_url: makeApiDocUrl('Music-Tagger'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.MusicTaggerChords]: new ModuleConfig({
    moduleKey: ModuleKey.MusicTaggerChords,
    info: ModuleInfoList[ModuleKey.MusicTaggerChords],
    api_doc_url: makeApiDocUrl('Music-Tagger'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.LoopExtractor]: new ModuleConfigLoopExtractor({
    moduleKey: ModuleKey.LoopExtractor,
    info: ModuleInfoList[ModuleKey.LoopExtractor],
    api_doc_url: makeApiDocUrl('Loop-Extractor'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'mp3'],
          minimumDurationSeconds: 0,
          maximumDurationSeconds: 30 * 60,
          channelModes: [ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.QualityCheck]: new ModuleConfig({
    moduleKey: ModuleKey.QualityCheck,
    info: ModuleInfoList[ModuleKey.QualityCheck],
    api_doc_url: makeApiDocUrl('Quality-Check'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.AudioTextAligner]: new ModuleConfigAudioTextAligner({
    moduleKey: ModuleKey.AudioTextAligner,
    info: ModuleInfoList[ModuleKey.AudioTextAligner],
    api_doc_url: makeApiDocUrl('Audio-Text-Aligner'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
      {
        name: InputName.LyricsUrl,
        title: 'Lyrics File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['txt'],
        }),
      },
    ],
  }),

  [ModuleKey.TimePitchControl]: new ModuleConfigTimePitchControl({
    moduleKey: ModuleKey.TimePitchControl,
    info: ModuleInfoList[ModuleKey.TimePitchControl],
    api_doc_url: makeApiDocUrl('Time-Pitch-Control'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
    paramList: [
      new ModuleParam<any>({
        name: 'pitchFactor',
        title: 'Pitch Factor',
        type: ModuleParamType.Slider,
        min: 0.5,
        max: 2,
        step: 0.01,
        defaultValue: 1,
        unit: 'x',
        tip: 'Define the pitch factor (between 0.5 and 2) to adjust the tone without affecting duration. (Default: 1)',
      }),
      new ModuleParam<any>({
        name: 'speedFactor',
        title: 'Speed Factor',
        type: ModuleParamType.Slider,
        min: 0.1,
        max: 10,
        step: 0.1,
        defaultValue: 1,
        unit: 'x',
        tip: 'Define the speed factor (between 0.1 and 10) to adjust the duration without affecting tone. (Default: 1)',
      }),
    ],
  }),

  [ModuleKey.TalkAnalyzer]: new ModuleConfigTalkAnalyzer({
    moduleKey: ModuleKey.TalkAnalyzer,
    info: ModuleInfoList[ModuleKey.TalkAnalyzer],
    api_doc_url: makeApiDocUrl('Talk-Analyzer'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          maximumDurationSeconds: 2 * 60 * 60,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.CoverDetector]: new ModuleConfig({
    moduleKey: ModuleKey.CoverDetector,
    info: ModuleInfoList[ModuleKey.CoverDetector],
    api_doc_url: makeApiDocUrl('Cover-Detector'),
    inputList: [
      {
        name: 'referenceUrl',
        title: 'Reference Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 1,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
      {
        name: 'suspectedUrlList',
        title: 'Suspected Audio Files',
        multiple: true,
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
          additionalInfo: 'max 20 tracks',
        }),
      },
    ],
    paramList: [
      new ModuleParam<any>({
        name: 'threshold',
        title: 'Threshold',
        type: ModuleParamType.Slider,
        min: 1,
        max: 100,
        step: 1,
        defaultValue: 95,
        tip: 'The threshold, between 0 and 100 (in %), is used to set the confidence score threshold. (Default: 95 )',
      }),
    ],
  }),

  [ModuleKey.InfringementDetector]: new ModuleConfig({
    moduleKey: ModuleKey.InfringementDetector,
    info: ModuleInfoList[ModuleKey.InfringementDetector],
    api_doc_url: makeApiDocUrl('Infringement-Detector'),
    inputList: [
      {
        name: 'referenceUrl',
        title: 'Reference Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 1,
          maximumDurationSeconds: 2 * 60 * 60,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
      {
        name: 'suspectedUrlList',
        title: 'Suspected Audio Files',
        multiple: true,
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 1,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.LoudnessAnalyzer]: new ModuleConfig({
    moduleKey: ModuleKey.LoudnessAnalyzer,
    info: ModuleInfoList[ModuleKey.LoudnessAnalyzer],
    api_doc_url: makeApiDocUrl('Loudness-Analyzer'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0.4,
          maximumDurationSeconds: 30 * 60,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.Converter]: new ModuleConfig({
    moduleKey: ModuleKey.Converter,
    info: ModuleInfoList[ModuleKey.Converter],
    api_doc_url: makeApiDocUrl('Converter'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
    paramsTip:
      "By default, all parameters are set to None, meaning it will retain your file's original settings for each parameter. Select at least one parameter you want to modify.",
    paramList: [
      new ModuleParam<any>({
        name: 'fileFormat',
        title: 'File Format',
        type: ModuleParamType.Button,
        options: [
          { value: 'None', title: 'None' },
          { value: 'wav', title: 'Wav' },
          { value: 'flac', title: 'Flac' },
          { value: 'mp3', title: 'Mp3' },
        ],
        defaultValue: 'None',
        tip: 'Desired output file format. By default, "None" = no modifications are applied to the format.',
      }),
      new ModuleParam<any>({
        name: 'sampleRate',
        title: 'Sample Rate',
        type: ModuleParamType.StepSlider,
        values: [
          'None',
          16000,
          22050,
          24000,
          32000,
          44100,
          48000,
          88200,
          96000,
          176400,
          192000,
        ],
        defaultValue: 'None',
        unit: 'Hz',
        tip: 'Desired output sample rate, in Hz. By default, "None" = no modifications are applied to the sample rate.',
      }),
      new ModuleParam<any>({
        name: 'bitRate',
        title: 'Bit Rate',
        type: ModuleParamType.StepSlider,
        values: ['None', 32000, 96000, 128000, 160000, 192000, 256000, 320000],
        defaultValue: 'None',
        unit: 'bits/s',
        tip: 'Desired output bitrate, in bits/sec (only applied for mp3 conversion). By default, "None" = no modifications are applied to the bitrate.',
      }),
      new ModuleParam<any>({
        name: 'channelMode',
        title: 'Channel Mode',
        type: ModuleParamType.Button,
        options: [
          { value: 'None', title: 'None' },
          { value: ChannelMode.Mono, title: 'Mono' },
          { value: ChannelMode.Stereo, title: 'Stereo' },
        ],
        defaultValue: 'None',
        tip: 'Desired output channel mode. By default, "None" = no modifications are applied to the channel mode.',
      }),
    ],
    minParamCount: 1,
  }),

  [ModuleKey.EditCut]: new ModuleConfigEditBox({
    moduleKey: ModuleKey.EditCut,
    info: ModuleInfoList[ModuleKey.EditCut],
    api_doc_url: makeApiDocUrl('Editing'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
    paramList: [
      new ModuleParam<any>({
        name: 'timeStamp',
        title: 'Time',
        type: ModuleParamType.Number,
        min: 0.001,
        max: 10000,
        step: 0.1,
        defaultValue: 3,
        unit: 's',
        tip: 'Define the moment where the file should be split, in seconds from the start.',
      }),
    ],
  }),

  [ModuleKey.EditFadeIn]: new ModuleConfigEditBox({
    moduleKey: ModuleKey.EditFadeIn,
    info: ModuleInfoList[ModuleKey.EditFadeIn],
    api_doc_url: makeApiDocUrl('Editing'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
    paramList: [
      new ModuleParam<any>({
        name: 'duration',
        title: 'Duration',
        type: ModuleParamType.Number,
        min: 0.001,
        max: 100,
        step: 0.1,
        defaultValue: 3,
        unit: 's',
        tip: 'Define the fade length in seconds. (Default : 3)',
      }),
    ],
  }),

  [ModuleKey.EditFadeOut]: new ModuleConfigEditBox({
    moduleKey: ModuleKey.EditFadeOut,
    info: ModuleInfoList[ModuleKey.EditFadeOut],
    api_doc_url: makeApiDocUrl('Editing'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
    paramList: [
      new ModuleParam<any>({
        name: 'duration',
        title: 'Duration',
        type: ModuleParamType.Number,
        min: 0.001,
        max: 100,
        step: 0.1,
        defaultValue: 3,
        unit: 's',
        tip: 'Define the fade length in seconds. (Default : 3)',
      }),
    ],
  }),

  [ModuleKey.EditMix]: new ModuleConfigEditBox({
    moduleKey: ModuleKey.EditMix,
    info: ModuleInfoList[ModuleKey.EditMix],
    api_doc_url: makeApiDocUrl('Editing'),
    inputList: [
      {
        name: InputName.AudioUrlList,
        title: 'Audio Files',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
        multiple: true,
        minMultipleInputCount: 2,
      },
    ],
  }),

  [ModuleKey.EditTransition]: new ModuleConfigEditBox({
    moduleKey: ModuleKey.EditTransition,
    info: ModuleInfoList[ModuleKey.EditTransition],
    api_doc_url: makeApiDocUrl('Editing'),
    inputList: [
      {
        name: 'audioUrl1',
        title: 'Audio File 1',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
      {
        name: 'audioUrl2',
        title: 'Audio File 2',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
    paramList: [
      new ModuleParam<any>({
        name: 'duration',
        title: 'Duration',
        type: ModuleParamType.Number,
        min: 0.001,
        max: 100,
        step: 0.1,
        defaultValue: 3,
        unit: 's',
        tip: 'Define the transition length in seconds. (Default : 3)',
      }),
      new ModuleParam<any>({
        name: 'method',
        title: 'Method',
        type: ModuleParamType.Button,
        options: [
          { value: 'none', title: 'None' },
          { value: 'fadein', title: 'Fade In' },
          { value: 'fadeout', title: 'Fade Out' },
          { value: 'crossfade', title: 'Crossfade' },
        ],
        defaultValue: 'crossfade',
      }),
    ],
  }),

  [ModuleKey.EditMute]: new ModuleConfigEditBox({
    moduleKey: ModuleKey.EditMute,
    info: ModuleInfoList[ModuleKey.EditMute],
    api_doc_url: makeApiDocUrl('Editing'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
  }),

  [ModuleKey.EditPanning]: new ModuleConfigEditBox({
    moduleKey: ModuleKey.EditPanning,
    info: ModuleInfoList[ModuleKey.EditPanning],
    api_doc_url: makeApiDocUrl('Editing'),
    inputList: [
      {
        name: InputName.AudioUrl,
        title: 'Audio File',
        ...makeInputFileFieldTipAndConstraints({
          formats: ['wav', 'flac', 'mp3'],
          minimumDurationSeconds: 0,
          maximumDurationSeconds: 2 * 60 * 60,
          channelModes: [ChannelMode.Mono, ChannelMode.Stereo],
        }),
      },
    ],
    paramList: [
      new ModuleParam<any>({
        name: 'pan',
        title: 'Panning',
        type: ModuleParamType.Slider,
        min: -100,
        max: 100,
        step: 1,
        defaultValue: 0,
        tip: 'Define the panning coefficient from left (-100) to right (+100). (Default : 0)',
      }),
    ],
  }),
}

/**
 * The list of modules sorted by service name.
 */
export const ModuleConfigListByServiceName: Record<string, ModuleConfig[]> =
  Object.values(ModuleConfigList).reduce(
    (acc, mod: ModuleConfig) => {
      const key = mod.info.serviceName
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(mod)
      return acc
    },
    {} as Record<string, ModuleConfig[]>
  )

/**
 * Return a module given its service name.
 */
export function getModuleByServiceName(
  serviceName: string
): ModuleConfig | undefined {
  return ModuleConfigListByServiceName[serviceName]?.at(0)
}

/**
 * Return a module given its URL path segment.
 */
export function getModuleByUrlPathSegment(
  segment: string
): ModuleConfig | undefined {
  return Object.values(ModuleConfigList).find(
    (mod) => mod.info.urlPathSegment === segment
  )
}

/**
 * Return a display name of a module given its service name.
 *
 * @param serviceName
 *    The name of the service in the back-end.
 * @returns
 *    The name to display if found, else the `serviceName` argument itself.
 */
export function getModuleDisplayName(serviceName: string): string {
  return (
    getModuleByServiceName(serviceName)?.info.displayModuleName ?? serviceName
  )
}

export default ModuleConfigList
